import React, { useEffect, useRef, useState } from 'react';
import { Popover, PopoverBody } from 'reactstrap';
import { ChevronDown, ChevronUp, Cross } from '@saleshandy/icons';
import {
  EnrichmentStatus,
  EnrichmentType,
} from '../../enums/csv-enrichment.enums';
import { accessibleOnClick } from '../../../../shared/utils';

type FilterOption = {
  key: string;
  label: string;
};

type FilterDropdownProps = {
  value: string;
  onChange: (v: string) => void;
  options: FilterOption[];
  placeholder: string;
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.key === value);
  const displayLabel = selectedOption?.label ?? placeholder;

  return (
    <div className="bs-csv-enrichment__fd-wrapper" ref={wrapperRef}>
      <div
        role="button"
        tabIndex={0}
        className={`bs-csv-enrichment__fd-trigger${
          isOpen ? ' bs-csv-enrichment__fd-trigger--open' : ''
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(e) => e.key === 'Enter' && setIsOpen((prev) => !prev)}
      >
        <span>{displayLabel}</span>
        {isOpen ? (
          <ChevronUp width={16} height={16} />
        ) : (
          <ChevronDown width={16} height={16} />
        )}
      </div>

      {isOpen && (
        <div className="bs-csv-enrichment__fd-list">
          {options.map((option) => {
            const isAll = option.key === '';
            const isSelected = option.key === value && value !== '';
            let optionClass = 'bs-csv-enrichment__fd-option';
            if (isAll) optionClass += ' bs-csv-enrichment__fd-option--all';
            if (isSelected) {
              optionClass += ' bs-csv-enrichment__fd-option--selected';
            }

            return (
              <div
                key={option.key || 'all'}
                role="option"
                aria-selected={option.key === value}
                tabIndex={0}
                className={optionClass}
                onClick={() => {
                  onChange(option.key);
                  setIsOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onChange(option.key);
                    setIsOpen(false);
                  }
                }}
              >
                {option.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

type EnrichmentFilterPopoverProps = {
  isOpen: boolean;
  target: string;
  onClose: () => void;
  statusFilter: EnrichmentStatus | null;
  typeFilter: EnrichmentType | null;
  onStatusFilterChange: (status: EnrichmentStatus | null) => void;
  onTypeFilterChange: (type: EnrichmentType | null) => void;
  onClearAllFilters: () => void;
  hasActiveFilters?: boolean;
};

const STATUS_OPTIONS: FilterOption[] = [
  { key: '', label: 'All Status' },
  { key: EnrichmentStatus.COMPLETED, label: 'Completed' },
  { key: EnrichmentStatus.PROCESSING, label: 'Processing' },
  { key: EnrichmentStatus.FAILED, label: 'Failed' },
  { key: EnrichmentStatus.CANCELLED, label: 'Cancelled' },
];

const TYPE_OPTIONS: FilterOption[] = [
  { key: '', label: 'All Types' },
  { key: EnrichmentType.COMPANY, label: 'Company' },
  { key: EnrichmentType.PEOPLE, label: 'People' },
];

const EnrichmentFilterPopover: React.FC<EnrichmentFilterPopoverProps> = ({
  isOpen,
  target,
  onClose,
  statusFilter,
  typeFilter,
  onStatusFilterChange,
  onTypeFilterChange,
  onClearAllFilters,
  hasActiveFilters,
}) => (
  <Popover
    placement="bottom-start"
    hideArrow
    isOpen={isOpen}
    target={target}
    toggle={onClose}
    className="bs-csv-enrichment__filter-popover"
  >
    <PopoverBody>
      <div className="bs-csv-enrichment__filter-header">
        <span className="filter-header">Filters</span>
        <button
          type="button"
          className="bs-csv-enrichment__filter-close"
          onClick={onClose}
        >
          <Cross width={18} height={18} />
        </button>
      </div>

      <div className="filter-popover-body">
        <div className="bs-csv-enrichment__filter-section">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="status">Status</label>
          <FilterDropdown
            value={statusFilter || ''}
            onChange={(v) => {
              onStatusFilterChange((v as EnrichmentStatus) || null);
              onClose();
            }}
            options={STATUS_OPTIONS}
            placeholder="All Status"
          />
        </div>

        <div className="bs-csv-enrichment__filter-section">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="type">Type</label>
          <FilterDropdown
            value={typeFilter || ''}
            onChange={(v) => {
              onTypeFilterChange((v as EnrichmentType) || null);
              onClose();
            }}
            options={TYPE_OPTIONS}
            placeholder="All Types"
          />
        </div>
      </div>
      {hasActiveFilters && (
        <div
          className="bs-csv-enrichment__clear-all-filters"
          {...accessibleOnClick(() => {
            onClearAllFilters();
            onClose();
          })}
        >
          Clear all filters
        </div>
      )}
    </PopoverBody>
  </Popover>
);

export default EnrichmentFilterPopover;
